import React, { useEffect, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Input, DateInput, Select, Radio } from "@components/form";
import { post, put, get } from "@utils/axios_api";
import { validateDuplicateId, validateDuplicateEmail } from "@utils/validation";
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { getCodeByName  } from '@utils/CommonCodes';
function MemberForm({ selectedData, onSaved, onAdd }) {
    const defaultFormValues = useMemo(() => ({
        memberId: "",
        memberNm: "",
        birthDt: "",
        genderCd: "",
        password: "",
        phoneNo: "",
        email: "",
        zipcode: "",
        address1: "",
        address2: "",
        bizRegNo: "",
        departmentNm: "",
        positionNm: "",
        status: getCodeByName("MEMBER_STATUS", "정상"),
        roleId: getCodeByName("ROLE", "수강생"),
    }),[]);

  const methods = useForm({
    defaultValues: defaultFormValues, // defaultValues : react-hook-form 초기값 설정 옵션
  });

  const { reset, setValue, handleSubmit, register, unregister  } = methods;
  const isEdit = useMemo(() => !!selectedData, [selectedData]); // 수정 모드, 등록 모드 판별
  const queryClient = useQueryClient();

  const { data:companyList } = useQuery({
    queryKey: ["adminSearchCompanyList"],
    queryFn: async () => {
      const response = await get("/admin/mem/companyList");
      return response;
    },
  });

  // 목록에서 선택한 값 세팅
  useEffect(() => {
    if (isEdit) {
      reset({...selectedData, password: ""});
    } else {
      reset(defaultFormValues);
    }
  }, [isEdit, selectedData, reset, defaultFormValues]);

  // 패스워드 등록/수정 모드 세팅
  useEffect(() => {
    unregister("password");
    // 다시 등록
    register("password", isEdit ? {} : { required: "비밀번호를 입력하세요." });
    // 수정 모드일 때 password 값은 항상 비워놓음
    setValue("password", "");
  }, [isEdit, register, unregister, setValue]);

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function (data) {
        setValue("zipcode", data.zonecode, { shouldValidate: true });
        setValue("address1", data.address, { shouldValidate: true });
      },
    }).open();
  };

  const handleAdd = () => {
    reset(defaultFormValues);
    onAdd && onAdd();
  };

  const onSubmit = async (formData) => {
    try {
      let response;
        if (isEdit) {
            if (!formData.password) {
                delete formData.password;
              }
            response = await put("/admin/mem/updateMember", formData);
        } else {
            response = await post("/admin/mem/insertMember", formData);
        }

        alert(response?.message || "저장 성공");
        await queryClient.invalidateQueries({ queryKey: ["selectAdminMemberList"] });

        // onSaved && onSaved();
    } catch (err) {
      console.error(err);
      alert("등록/수정 실패");
    }
  };

  const onError = () => {
    alert("필수 입력란을 확인하세요.");
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)} >
        <div className="section-tit-wrap">
            <h3 className="border-left-primary">&nbsp;회원 등록/수정</h3>
            <div className="btn-right-box">
            <ul>
                <li><button type="button" title="추가" className="btn-add" onClick={handleAdd}>추가</button></li>
                <li><button type="submit" title="저장" className="btn-save">저장</button></li>
            </ul>
            </div>
        </div>

        <table className="table-write">
            <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "40%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "40%" }} />
            </colgroup>
            <tbody>
            <tr>
                <th>아이디<span className="red">*</span></th>
                <td>
                <Input
                    name="memberId"
                    placeholder="아이디"
                    className="input-text"
                    style={{ width: "100%" }}
                    readOnly={isEdit}
                    rules={{ 
                        required:"아이디를 입력하세요.",
                        pattern: {
                            value: /^[A-Za-z0-9]+$/,
                            message: "영문과 숫자만 입력 가능합니다."
                          },
                        validate: (value) => {
                            if (selectedData && value === selectedData.memberId) {
                                return true;
                              }
                              return validateDuplicateId(value);
                        }
                     }}
                />
                </td>
                <th>이름<span className="red">*</span></th>
                <td>
                <Input
                    name="memberNm"
                    placeholder="이름"
                    className="input-text"
                    style={{ width: "100%" }}
                    rules={{ required:true }}
                    />
                </td>
            </tr>
            <tr>
                <th>전화번호<span className="red">*</span></th>
                <td>
                <Input
                    name="phoneNo"
                    placeholder="ex)010-0123-4567"
                    className="input-text"
                    style={{ width: "100%" }}
                    rules={{
                      required: "전화번호를 입력하세요.",
                      pattern: {
                        value: /^\d{2,3}-\d{3,4}-\d{4}$/,
                        message: "전화번호는 숫자와 하이픈(-)으로 구성된 형식이어야 합니다. ex) 010-1234-5678"
                      }
                    }}
                    />
                </td>
                <th>이메일<span className="red">*</span></th>
                <td>
                <Input
                    name="email"
                    placeholder="이메일"
                    className="input-text"
                    style={{ width: "100%" }}
                    rules={{
                        required: "이메일을 입력하세요.",
                        pattern: {
                            value: /^[A-Za-z0-9@.]+$/,
                            message: "이메일 형태로 작성해주세요"
                          },
                        validate: (value) => {
                            if (selectedData && value === selectedData.email) {
                              return true;
                            }
                            return validateDuplicateEmail(value);
                          },
                    }}
                    />
                </td>
            </tr>
            <tr>
                <th>비밀번호{!isEdit && <span className="red">*</span>}
                </th>
                <td colSpan={3}>
                    <Input
                        name="password"
                        placeholder="비밀번호"
                        type="password"
                        className="input-text"
                        style={{ width: "100%" }}
                    />
                </td>
            </tr>
            <tr>
            <th>소속기업</th>
            <td colSpan={3}>
              <Select
                  name="bizRegNo"
                  options={companyList?.result}
                  valueKey="bizRegNo"  
                  labelKey="companyNm"
                  defaultOption="전체" 
                  className="input-select"
                  style={{width:"45%"}}

              />
            </td>
            </tr>
            <tr>
                <th>생년월일 <span className="red">*</span></th>
                <td>
                <DateInput 
                    name="birthDt"
                    style={{width:"100%"}}
                    rules={{ required:true }} 
                />
                </td>
                <th>성별</th>
                <td>
                <div style={{display:"flex", gap:"15px"}}>
                  <Radio name="genderCd" label="남자" value="M" />
                  <Radio name="genderCd" label="여자" value="F" />
                </div>
                </td>
            </tr>
            <tr>
                <th>부서</th>
                <td>
                <Input
                    name="departmentNm"
                    placeholder="부서명"
                    className="input-text"
                    style={{ width: "100%" }}
                    />
                </td>
                <th>직급</th>
                <td>
                <Input
                    name="positionNm"
                    placeholder="사원/대리/과장..."
                    className="input-text"
                    style={{ width: "100%" }}
                    />
                </td>
            </tr>

            <tr>
                <th>주소</th>
                <td colSpan={3}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: '8px', display:'flex' }}>
                      <Input
                        name="zipcode"
                        placeholder="우편번호"
                        readOnly
                        className="input-text"
                        style={{width:"100%"}}
                      />
                      <button
                        type="button"
                        className="btn-add btn-form-search"
                        onClick={handleAddressSearch}
                        style={{marginLeft:"10px"}} >
                        검색
                      </button>
                    </div>
                    <div>
                      <Input
                        name="address1"
                        placeholder="주소"
                        readOnly
                        className="input-text"
                        maxLength={30}
                        style={{width:"100%", marginBottom:"10px"}}
                      />
                      <Input
                        name="address2"
                        placeholder="상세주소를 입력하세요"
                        className="input-text"
                        maxLength={30}
                        style={{width:"100%"}}
                      />
                    </div>
                  </div>
                </td>
            </tr>

            <tr>
              <th>상태</th>
              <td colSpan="3">
              <div style={{display:"flex", gap:"15px"}}>
                <Radio name="status" label="승인대기" value={getCodeByName("MEMBER_STATUS", "승인대기")} />
                <Radio name="status" label="정상" value={getCodeByName("MEMBER_STATUS", "정상")} />
                <Radio name="status" label="정지" value={getCodeByName("MEMBER_STATUS", "정지")} />
                <Radio name="status" label="휴면" value={getCodeByName("MEMBER_STATUS", "휴면")} />
              </div>
              </td>
            </tr>
            <tr>
              <th>관리자여부</th>
              <td colSpan="3">
              <div style={{display:"flex", gap:"15px"}}>
                <Radio name="roleId" label="관리자" value={getCodeByName("ROLE", "관리자")} />
                <Radio name="roleId" label="수강생" value={getCodeByName("ROLE", "수강생")} />
              </div>
              </td>
            </tr>
            </tbody>
        </table>
      </form>
    </FormProvider>
  );
}

export default MemberForm;
